import { logger } from 'src/analytics/KatalLogger';
import { ValidationErrorDetail, ValidationStatusEntity } from 'src/models/XptGenericModels';
import { ForecastTemplateDataValidationStatus, VALIDATION_NOT_INITIATED } from 'src/models/ForecastModels';
import ForecastValidationMessages from '../forecast-utils/ValidationMessages';

// Define common validation states as reusable constants
export const NOT_INITIATED_STATE = (message: string): ValidationStatusEntity => ({
  ...VALIDATION_NOT_INITIATED,
  validationMessage: message
});

export const SUCCESS_STATE = (message: string): ValidationStatusEntity => ({
  colorOverride: 'green',
  validationStatus: 'success',
  validationMessage: message,
  validationDefaultMessage: '',
  validationErrorDetails: []
});

export const STOPPED_NO_MODIFIED_ROWS_STATE: ValidationStatusEntity = {
  colorOverride: 'grey',
  validationStatus: 'stopped',
  validationMessage: ForecastValidationMessages.NO_MODIFIED_ROWS,
  validationDefaultMessage: '',
  validationErrorDetails: []
};

// Export these constants directly so they can be imported elsewhere
export const INITIAL_VALIDATION_STATUS: ForecastTemplateDataValidationStatus = {
  HeadersMatching: NOT_INITIATED_STATE('Header validation'),
  FileChangeDetectionValidation: NOT_INITIATED_STATE('File change detection'),
  BudgetOwnerValidation: NOT_INITIATED_STATE('Budget Owner validation'),
  CostCenterValidation: NOT_INITIATED_STATE('Cost Center validation'),
  ForecastMonthValidation: NOT_INITIATED_STATE('Forecast Month validation'),
  MandatoryFieldValidation: NOT_INITIATED_STATE('Mandatory fields validation'),
  NonEditableFieldValidations: NOT_INITIATED_STATE('Non editable fields validation'),
  UnAuthorizedRows: NOT_INITIATED_STATE('Budget owner validation'),
  SegmentsValidation: NOT_INITIATED_STATE('Segments validation'),
  DuplicateRecordValidation: NOT_INITIATED_STATE('Duplicate validation'),
  RepeatedRecordValidation: NOT_INITIATED_STATE('Repeated validation'),
  BudgetTypeAccountMappingValidation: NOT_INITIATED_STATE('Budget Type & Account validation'),
  CurrencyFieldValidation: NOT_INITIATED_STATE('Currency field validation'),
  BudgetOwnerCostCenterValidation: NOT_INITIATED_STATE('Budget Owner & Segment validation'),
  DMMBusinessSegmentValidation: NOT_INITIATED_STATE('DMM Business Segment validation'),
  BudgetEditableMonthsValidation: NOT_INITIATED_STATE('Budget editable months validation')
};

export const NO_MODIFIED_ROWS: ForecastTemplateDataValidationStatus = {
  HeadersMatching: SUCCESS_STATE(ForecastValidationMessages.HEADER_VALIDATION_SUCCESS),
  FileChangeDetectionValidation: STOPPED_NO_MODIFIED_ROWS_STATE,
  BudgetOwnerValidation: STOPPED_NO_MODIFIED_ROWS_STATE,
  CostCenterValidation: STOPPED_NO_MODIFIED_ROWS_STATE,
  ForecastMonthValidation: STOPPED_NO_MODIFIED_ROWS_STATE,
  MandatoryFieldValidation: STOPPED_NO_MODIFIED_ROWS_STATE,
  NonEditableFieldValidations: STOPPED_NO_MODIFIED_ROWS_STATE,
  SegmentsValidation: STOPPED_NO_MODIFIED_ROWS_STATE,
  UnAuthorizedRows: STOPPED_NO_MODIFIED_ROWS_STATE,
  DuplicateRecordValidation: STOPPED_NO_MODIFIED_ROWS_STATE,
  RepeatedRecordValidation: STOPPED_NO_MODIFIED_ROWS_STATE,
  BudgetTypeAccountMappingValidation: STOPPED_NO_MODIFIED_ROWS_STATE,
  CurrencyFieldValidation: STOPPED_NO_MODIFIED_ROWS_STATE,
  BudgetOwnerCostCenterValidation: STOPPED_NO_MODIFIED_ROWS_STATE,
  DMMBusinessSegmentValidation: STOPPED_NO_MODIFIED_ROWS_STATE,
  BudgetEditableMonthsValidation: STOPPED_NO_MODIFIED_ROWS_STATE
};

export const INITIAL_SUBMIT_STATUS: ValidationStatusEntity = {
  colorOverride: 'grey',
  validationStatus: 'pending',
  validationMessage: 'Not Initiated',
  validationDefaultMessage: '',
  validationErrorDetails: []
};

// Status value constants for use in validation status objects
export const VALIDATION_STATUS = {
  SUCCESS: 'success',
  ERROR: 'error',
  PENDING: 'pending',
  STOPPED: 'stopped',
  IN_PROGRESS: 'in-progress'
};

// Color constants for validation status
export const VALIDATION_COLOR = {
  GREEN: 'green',
  RED: 'red',
  GREY: 'grey',
  BLUE: 'blue'
};

/**
 * Base validation service that provides common validation functionality
 */
export class ValidationService {
  /**
   * Creates a success validation status
   * @param message Success message
   * @param defaultMessage Default message (optional)
   * @returns ValidationStatusEntity
   */
  protected createSuccessStatus(message: string, defaultMessage: string = ''): ValidationStatusEntity {
    return {
      colorOverride: 'green',
      validationStatus: 'success',
      validationMessage: message,
      validationDefaultMessage: defaultMessage,
      validationErrorDetails: []
    };
  }

  /**
   * Creates an error validation status
   * @param message Error message
   * @param defaultMessage Default message
   * @param errorDetails Array of error details
   * @returns ValidationStatusEntity
   */
  protected createErrorStatus(message: string, defaultMessage: string, errorDetails: ValidationErrorDetail[]): ValidationStatusEntity {
    return {
      colorOverride: 'red',
      validationStatus: 'error',
      validationMessage: message,
      validationDefaultMessage: defaultMessage,
      validationErrorDetails: errorDetails
    };
  }

  /**
   * Creates a stopped validation status
   * @param message Status message
   * @returns ValidationStatusEntity
   */
  protected createStoppedStatus(message: string): ValidationStatusEntity {
    return {
      colorOverride: 'grey',
      validationStatus: 'stopped',
      validationMessage: message,
      validationDefaultMessage: '',
      validationErrorDetails: []
    };
  }

  /**
   * Gets the line item ID message part for error messages
   * @param row Data row
   * @param lineItemIdField Field name for line item ID
   * @returns String with line item ID or empty string
   */
  protected getLineItemIdMessagePart(row: any, lineItemIdField: string): string {
    const lineItemId = row[lineItemIdField];
    return lineItemId ? `(ID: ${lineItemId})` : '';
  }

  /**
   * Logs validation errors for debugging
   * @param validationType Type of validation
   * @param errorDetails Error details
   */
  protected logValidationErrors(validationType: string, errorDetails: ValidationErrorDetail[]): void {
    if (errorDetails.length > 0) {
      logger.debug('Validation failed', {
        validationType,
        errorDetails: JSON.stringify(errorDetails, null, 2)
      });
    }
  }
}
